import * as Sentry from "@sentry/react";
import EventEmitter from "eventemitter3";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Socket as IoSocket } from "socket.io-client";
import { io } from "socket.io-client";

import { trackSchema, type Track } from "@lib/types";
import { toast } from "react-toastify";

import type { Client } from "@engine/Client";
import type { ActionType, UnconfirmedActionMessage } from "@engine/core";
import {
  ClientStorageAdapter,
  messageToClientSchema,
  type MessageToClient,
  type MessageToServer,
} from "@engine/network";
import { Action } from "@engine/round/actions";
import type { RoundAggregate } from "@engine/round/aggregate";
import SPAStorageAdapterWrapper from "@engine/storageAdapters/SPAStorageAdapterWrapper";
import type { UserProfiles } from "@lib/types";
import { anonymizeAggregate } from "@lib/utils/anonymization";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { DisconnectDescription } from "socket.io-client/build/esm/socket";
import { z } from "zod";

type RoundClient = Client;

export type RoundData = {
  client: RoundClient;
  userProfiles: UserProfiles;
};

type SocketStatus =
  | "connected"
  | {
      reason: IoSocket.DisconnectReason;
      description?: DisconnectDescription | undefined;
      willReconnect: boolean;
    }
  | null;

export type State = {
  underlyingUserId: string;
  actingAsUserId: string;
  round: RoundAggregate;
  userProfiles: UserProfiles;
  mode: AppMode;
  unconfirmedActions: Promise<
    {
      createdAt: number;
      message: UnconfirmedActionMessage;
    }[]
  >;
  subscribe(eventType: ActionType, cb: (action: Action) => void): void;
  unsubscribe(eventType: ActionType, cb: (eventDetail: Action) => void): void;
  socketStatus: SocketStatus;
  setMode: (mode: AppMode) => Promise<void>;
  logStorage: () => void;
  getRound: () => RoundAggregate;
  retryUnconfirmedActions: () => Promise<Error | null>;
  clearUnconfirmedActions: () => Promise<void>;
  reportError: () => void;
  storage: ClientStorageAdapter;
  disconnect: () => void;
};

export type AppMode = {
  adminCanEdit: boolean;
  viewAsTrackMember: Track | null;
  viewAsUserId: string | null;
  // The user is not allowed to dispatch actions
  readOnly: boolean;
  // We don't process new actions from the server
  disconnected: boolean;
};

const appModeSchema = z.object({
  adminCanEdit: z.boolean(),
  viewAsTrackMember: z.union([trackSchema, z.null()]),
  viewAsUserId: z.union([z.string(), z.null()]),
});

const APP_MODE_STORAGE_KEY = "appMode";

function getModeFromLocalStorage(): AppMode | null {
  if (typeof localStorage === "undefined") return null;
  const stored = localStorage.getItem(APP_MODE_STORAGE_KEY);
  if (!stored) return null;
  try {
    const result = appModeSchema.safeParse(JSON.parse(stored));
    if (result.success) {
      return {
        ...result.data,
        readOnly: false,
        disconnected: false,
      };
    }
  } catch {
    // ignore malformed local storage
  }
  return null;
}

function setModeToLocalStorage(mode: AppMode) {
  if (typeof localStorage === "undefined") return;
  try {
    const valid = appModeSchema.parse(mode);
    localStorage.setItem(APP_MODE_STORAGE_KEY, JSON.stringify(valid));
  } catch {
    // ignore invalid mode values
  }
}

type ServerToClientEvents = {
  message: (msg: MessageToClient, cb: () => void) => void;
  connection: (socket: Socket) => void;
};

type ClientToServerEvents = {
  message: (msg: MessageToServer, cb: () => void) => void;
  connection: (socket: Socket) => void;
};

type Socket = IoSocket<ServerToClientEvents, ClientToServerEvents>;

class ClientWrapper {
  // Actions which have been applied to the storage adapter but not yet emitted
  // (We emit them after the next render so the evaluations cache has the latest
  // data)
  private pendingActions: Action[] = [];

  client: RoundClient;
  userProfiles: UserProfiles;

  setState?: (state: State) => void;

  mode: AppMode = {
    adminCanEdit: false,
    viewAsTrackMember: null,
    viewAsUserId: null,
    readOnly: false,
    disconnected: false,
  };

  emitter = new EventEmitter();
  clientMessageHandler!: (msg: MessageToClient) => void;
  socket: Socket;
  disconnectDetails: {
    reason: IoSocket.DisconnectReason;
    description?: DisconnectDescription | undefined;
    willReconnect: boolean;
  } | null = null;

  // When there's a new client, connect it to our socket.
  // userProfiles probably hasn't changed, but there's a chance it has
  // so we might as well update it.
  connect({ client, userProfiles }: RoundData) {
    this.client = client;
    this.userProfiles = userProfiles;

    if (!this.mode.disconnected) {
      this.client.connect(
        (msg) => this.handleClientMessageToServer(msg),
        (handler) => {
          this.clientMessageHandler = handler;
        },
        () => this.update(),
        (action) => this.emitAction(action.payload),
      );
    }
  }

  constructor({ client, userProfiles }: RoundData) {
    this.client = client;
    this.userProfiles = userProfiles;

    const storedMode = getModeFromLocalStorage();
    if (storedMode) {
      this.client.setReadOnly(storedMode.readOnly);
      this.client.actingAsUserId =
        storedMode.viewAsUserId ?? this.client.underlyingUserId;
      this.mode = storedMode;
    }

    this.client.connect(
      (msg) => this.handleClientMessageToServer(msg),
      (handler) => {
        this.clientMessageHandler = handler;
      },
      () => this.update(),
      (action) => this.emitAction(action.payload),
    );

    const { aggregateId: roundId, id: clientId } = client;
    this.socket = io(`/${roundId}/${clientId}`, { retries: 2 });
    this.socket.on("connect", () => {
      this.client.register();
      void this.client.sendUnconfirmedActions();
      this.update();
    });
    this.socket.on(
      "disconnect",
      (
        reason: IoSocket.DisconnectReason,
        description?: DisconnectDescription | undefined,
      ) => {
        this.disconnectDetails = {
          reason,
          description,
          willReconnect: this.socket.active,
        };
        console.log("Disconnected from server", reason, description);
        this.update();
      },
    );

    this.socket.on("message", async (unparsedMessage) => {
      try {
        const message = messageToClientSchema.parse(unparsedMessage);
        await this.client.handleMessage(message);
      } catch (error) {
        console.error("Error handling message from server", unparsedMessage);
        console.error(error);
        Sentry.captureException(error);
        throw error;
      }
    });
  }

  emitAction(action: Action) {
    // do after the next render. When processing the client queue
    // this.update() may or may not be called, if it is called we want this to
    // always run after the render triggered by this.update(), because subscribers
    // to the action (of which useEvaluatorEvaluationsCache is the only one), won't
    // have current data until the render has run.
    this.pendingActions.push(action);
  }

  drainPendingActions() {
    const actions = this.pendingActions;
    this.pendingActions = [];
    for (const action of actions) {
      this.emitter.emit(action.type, action);
    }
  }

  sendMessageToClient(msg: MessageToClient) {
    this.clientMessageHandler(msg);
  }

  async handleClientMessageToServer(msg: MessageToServer) {
    try {
      this.socket.timeout(5000).emit("message", msg, (err) => {
        if (err) {
          console.warn("Error sending message to server", err);
          console.log("Retrying message to server", msg);
          void this.handleClientMessageToServer(msg);
        }
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
      switch (msg.type) {
        case "PerformAction": {
          this.sendMessageToClient({
            type: "PerformActionFailed",
            actionId: msg.action.id,
            message: (error as Error).message,
          });
          break;
        }
        case "Register":
          toast.error("Error registering " + (error as Error).message);
          break;
        default:
          assertUnreachable(msg);
      }
    }
  }

  update() {
    if (!this.setState) {
      return;
    }

    this.setState(this.getState());
  }

  setSetState(setState: (state: State) => void) {
    this.setState = setState;
    this.update();
  }

  getState(): State {
    const setMode = async (mode: AppMode) => {
      const unconfirmedActions =
        await this.client.storage.getUnconfirmedActions();
      if (unconfirmedActions.length > 0) {
        toast.error("Cannot set mode while there are unconfirmed actions");
        return;
      }
      this.client.setReadOnly(mode.readOnly);
      this.client.actingAsUserId =
        mode.viewAsUserId ?? this.client.underlyingUserId;
      this.mode = mode;
      setModeToLocalStorage(mode);

      this.update();
    };

    const logStorage = () => {
      console.log(this.client.storage);
    };

    const agg = this.client.getAggregate();
    let viewerTrack: Track;
    if (this.mode.viewAsTrackMember) {
      viewerTrack = this.mode.viewAsTrackMember;
    } else if (this.mode.viewAsUserId) {
      viewerTrack = agg.members[this.mode.viewAsUserId].track;
    } else {
      viewerTrack = agg.members[this.client.underlyingUserId].track;
    }

    return {
      underlyingUserId: this.client.underlyingUserId,
      actingAsUserId: this.client.actingAsUserId,
      round: anonymizeAggregate(viewerTrack, agg),
      userProfiles: this.userProfiles,
      mode: this.mode,
      subscribe: this.subscribe.bind(this),
      unsubscribe: this.unsubscribe.bind(this),
      socketStatus: this.socket.connected
        ? "connected"
        : this.disconnectDetails,
      setMode,
      unconfirmedActions: this.client.storage.getUnconfirmedActions(),
      logStorage,
      getRound: () =>
        anonymizeAggregate(viewerTrack, this.client.getAggregate()),
      retryUnconfirmedActions: () => this.client.retryUnconfirmedActions(),
      clearUnconfirmedActions: async () => {
        await this.client.clearUnconfirmedActions();
        this.update();
      },
      reportError: () => {
        const error = new Error("User clicked report error button");
        if (this.client.storage instanceof SPAStorageAdapterWrapper) {
          void this.client.storage.captureException(error);
        } else {
          Sentry.captureException(error);
        }
      },
      disconnect: () => {
        this.client.handleMessage = async () => {};
        this.socket.disconnect();
        this.mode.disconnected = true;
        this.mode.readOnly = true;
        this.update();
      },
      storage: this.client.storage,
    };
  }

  subscribe<T extends ActionType>(
    actionType: T,
    cb: (action: Action) => void,
  ): void {
    this.emitter.on(actionType, cb);
  }

  unsubscribe<T extends ActionType>(
    actionType: T,
    cb: (eventDetail: Action) => void,
  ): void {
    this.emitter.off(actionType, cb);
  }

  async dispatch(action: Action): Promise<Error | null> {
    if (this.mode.readOnly) {
      toast.error("Cannot dispatch action in read-only mode");
      return new Error("Cannot dispatch action in read-only mode");
    } else {
      const result = await this.client.dispatch(action);
      if (result instanceof Error) {
        Sentry.captureException(result);
        return result;
      }
      return null;
    }
  }

  async dispatchAndIgnore(action: Action): Promise<void> {
    const error = await this.dispatch(action);
    if (!error) {
      return;
    }

    toast.error("Error dispatching action " + error.message);
  }
}

export type Dispatch = (action: Action) => Promise<void>;

export function useEventSourcing(roundData: RoundData): [State, Dispatch] {
  // We can't initialize the imperative state until after the first render
  const imperativeStateRef = useRef<ClientWrapper | null>(null);

  // lazily initialize imperative state
  // https://stackoverflow.com/a/74034441
  const getImperativeState = useCallback(
    function () {
      const imperativeState = imperativeStateRef.current;
      if (imperativeState) {
        imperativeState.connect(roundData);
        return imperativeState;
      }

      const newImperativeState = new ClientWrapper(roundData);
      imperativeStateRef.current = newImperativeState;
      return newImperativeState;
    },
    [roundData],
  );

  const [state, setState] = useState<State>(() =>
    getImperativeState().getState(),
  );

  const dispatch = (action: Action) =>
    getImperativeState().dispatchAndIgnore(action);

  // Don't give ImperativeState a setState until after the first render,
  // setState can't be called during initialization
  useEffect(() => {
    getImperativeState().setSetState(setState);
  }, [getImperativeState]);

  // Flush the action queue after every render
  useEffect(() => {
    getImperativeState().drainPendingActions();
  });

  return [state, dispatch] as const;
}
